/**
 * THE MAIN CHAT — the one conversation, and the only one there will be.
 *
 * THE LAYOUT IS THE OWNER'S DECISION OF 2026-10-17. The conversation fills the
 * content area, only the messages scroll, and the composer is always at the
 * bottom. There is no explanatory paragraph above the transcript and no
 * technical record in the reading path: the title row is the page's name and
 * one About control, and everything the earlier chat said in the open is one
 * press away.
 *
 * FIVE THINGS THIS SURFACE REFUSES TO DO, EACH BECAUSE THE ALTERNATIVE IS A
 * LIE THE OWNER WOULD HAVE TO CATCH HIMSELF:
 *
 *   1. IT DOES NOT FAKE STREAMING. `/gateway/ask` returns one JSON body when
 *      it is finished. There is no token stream behind it, so there is no typing
 *      animation here: what is shown is a request that is genuinely
 *      outstanding, with the time it has been outstanding, and nothing more.
 *
 *   2. IT DOES NOT CALL AN ABORTED REQUEST A CANCELLATION. Abandoning the
 *      request in this browser stops this browser waiting. It does not stop
 *      anything Scout is doing, and the surface says exactly that.
 *
 *   3. IT DOES NOT INVENT AN ANSWER THAT HAS NOT ARRIVED. A turn that is
 *      waiting shows as waiting; a turn whose answer was recorded but not yet
 *      read shows as unread, not as empty.
 *
 *   4. IT DOES NOT RENDER SCOUT'S WORDS AS MARKUP. Everything is text nodes —
 *      Preact escapes by default and nothing here opts out. There is no
 *      `dangerouslySetInnerHTML`, no `innerHTML`, and no markdown parser: Scout
 *      writes prose, and prose that happens to contain `<` is prose.
 *
 *   5. IT DOES NOT HIDE A TRUNCATED ANSWER. `completion` is rendered when it
 *      says the generative work did not finish, because an answer cut at its
 *      budget and read as finished is the record telling a lie.
 *
 * SCROLLING RESPECTS THE READER. A new turn scrolls into view when the reader
 * is at the bottom; when they are reading older messages it does not jump, and
 * a "Jump to latest" control counts what arrived. Loading older history holds
 * the reader's place instead of throwing them hundreds of turns away.
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
	adoptMainChat,
	isLong,
	mintRequestId,
	formatTurnTime,
	readOlderMessages,
	resolveMainChat,
	resolveSpaceChat,
	DISCUSS_ROUTE_SECTION,
	sendTurn,
	scoutTurn,
	type ChatMessage,
	type MainChatResolution,
	type SpaceChatResolution,
} from '../chat';
import { markdownToPlainText, Markdown } from '../markdown';
import { isAuthenticationFailure, isNotTheOwner, type GatewayError } from '../gateway';
import { createLoadable } from '../resource';
import { Composer, type AttachedMaterial } from '../composer';
import { MaterialList } from '../material-view';
import { navigate, route } from '../router';
import { parsePath } from '../paths';
import { linkRecordRefs } from '../record-links';
import { PageHeader, Technical } from './about';
import { ResearchStrip } from './research-strip';

/** A turn keeps everything it was sent with, so a retry sends the SAME turn.
 *  Dropping the attachments on retry would answer a different question than
 *  the one that failed — and it would look like it worked. */
interface PendingTurn {
	requestId: string;
	question: string;
	materials: AttachedMaterial[];
}

type Pending =
	| { kind: 'none' }
	| ({ kind: 'sending'; startedAt: number } & PendingTurn)
	| ({ kind: 'failed'; error: GatewayError } & PendingTurn)
	| ({ kind: 'inFlight'; note: string } & PendingTurn)
	/** THE RECORD CANNOT SAY WHAT HAPPENED. Kept apart from `failed` because it
	 *  is not one, and apart from `inFlight` because waiting will not resolve
	 *  it. The one action that does is the Owner's, and it is offered rather
	 *  than taken. */
	| ({ kind: 'uncertain'; note: string; how: string } & PendingTurn);

const COMPLETION_NOTICES: Record<string, string> = {
	INCOMPLETE_OUTPUT_LIMIT:
		'Scout’s answer was cut off at its generation budget. What is missing was never written down — the record does not have it either.',
	INCOMPLETE_FAILURE:
		'Scout’s answer did not finish: the generative step failed. What is shown is what was produced.',
	INCOMPLETE_UNKNOWN:
		'Scout could not tell whether this answer finished. It is shown as unfinished rather than as complete.',
};

/** THE ATTACHMENTS A WAITING OR FAILED TURN CARRIES. A failed send that showed
 *  only the question would erase the half of the draft that cost the Owner the
 *  most effort; naming the files here is what keeps that from being silent. */
function PendingAttachments({ materials }: { materials: AttachedMaterial[] }) {
	if (materials.length === 0) return null;
	return (
		<p class="muted small">
			Attached: {materials.map((material) => material.fileName ?? material.title).join(' · ')}
		</p>
	);
}

/** How close to the bottom still counts as "following the conversation". */
const STICK_THRESHOLD_PX = 120;

function Message({
	message,
	onDiscuss,
}: {
	message: ChatMessage;
	onDiscuss: (message: ChatMessage) => void;
}) {
	const [expanded, setExpanded] = useState(false);
	const long = isLong(message.content);
	// A COLLAPSED TURN SHOWS ITS WORDS, NOT ITS SYNTAX. The preview is the
	// plain reading of the message cut at the same bound; slicing the raw
	// Markdown instead could leave a half-written `**` on screen, which is the
	// exact defect this renderer exists to remove.
	const preview = long && !expanded ? `${markdownToPlainText(message.content).slice(0, 1_200)}…` : null;
	const shown = preview ?? message.content;
	const completion = message.completion;
	// THE RUN THIS SUMMARY BELONGS TO. The refs carry both `run:<id>` and
	// `run:<id>:report`; the report ref is not a page. The link is rendered from
	// the record, never guessed from the prose.
	const summaryRunRef =
		message.triggerKind === 'RESEARCH_WINDOW_SUMMARY'
			? (message.refs.find((ref) => /^run:[0-9a-f]{8}$/.test(ref)) ?? null)
			: null;

	return (
		<article class={`turn turn-${message.role.toLowerCase()}`} data-sequence={message.sequence}>
			<header class="turn-head">
				<span class="turn-who">{message.role === 'OWNER' ? 'You' : 'Scout'}</span>
				{/* THE EXACT INSTANT IS IN THE ATTRIBUTE AND THE TOOLTIP; what is
				    PRINTED is the same moment in the reader's own conventions. */}
				<time class="turn-when" dateTime={message.createdAt} title={message.createdAt}>
					{formatTurnTime(message.createdAt)}
				</time>
				{/* THE TRUTHFULNESS LABELS, QUIET AND PRESENT. They are not
				    technical clutter: they are the record's own statement of how
				    an answer was produced, and the one place a reader can tell a
				    model's phrasing from a deterministic reading. */}
				{message.reasoning === 'EXPLORATORY' && (
					<span class="turn-label" title="Bounded reasoning over your question, not a report over records">
						exploratory
					</span>
				)}
				{message.reasoning === 'EVIDENCE_REPORT' && (
					<span class="turn-label" title="A report restating durable records">
						evidence report
					</span>
				)}
				{message.phrasing === 'MODEL' && (
					<span class="turn-label" title="A model phrased this sentence">
						phrased
					</span>
				)}
				{message.triggerKind === 'OWNER_CLICK_SCOUT_TURN' && (
					<span class="turn-label" title="Scout chose to speak after you clicked “Scout, cerita”">
						you invited this
					</span>
				)}
				{message.triggerKind === 'RESEARCH_WINDOW_SUMMARY' && (
					<span class="turn-label" title="Scout's own account of the research window">
						research summary
					</span>
				)}
				{message.answerOutlook === 'IN_FLIGHT' && (
					<span class="turn-label turn-pending">waiting on an answer</span>
				)}
				{message.answerOutlook === 'UNCERTAIN' && (
					<span class="turn-label turn-failed">the record cannot say whether this was answered</span>
				)}
				{message.answerOutlook === 'FAILED' && (
					<span class="turn-label turn-failed">no answer was recorded</span>
				)}
			</header>

			{/* SCOUT'S WORDS ARE RENDERED AS THE LANGUAGE THEY WERE WRITTEN IN.
			    The Owner's own text stays literal — emphasis the Owner typed is
			    characters the Owner typed — and a collapsed Scout turn shows a
			    plain preview until it is opened. */}
			{message.role === 'SCOUT' && preview === null ? (
				<Markdown
					content={message.content}
					class="turn-body turn-markdown"
					transform={linkRecordRefs}
				/>
			) : (
				<div class="turn-body">{shown}</div>
			)}
			{long && (
				<button type="button" class="button ghost small" onClick={() => setExpanded(!expanded)}>
					{expanded ? 'Show less' : `Show all ${message.content.length.toLocaleString()} characters`}
				</button>
			)}

			{completion !== null && completion !== 'COMPLETE' && (
				<p class="turn-notice small" role="status">
					{COMPLETION_NOTICES[completion] ?? `This answer ended as ${completion}.`}
				</p>
			)}

			{summaryRunRef !== null && (
				<p class="turn-notice small">
					<a
						href={`/research/window/${encodeURIComponent(summaryRunRef)}`}
						onClick={(event) => {
							event.preventDefault();
							navigate(parsePath(`/research/window/${encodeURIComponent(summaryRunRef)}`));
						}}
					>
						Open the full research record →
					</a>
				</p>
			)}

			{message.role === 'OWNER' && message.materials !== undefined && message.materials.length > 0 && (
				<MaterialList materials={message.materials} />
			)}

			{message.references !== undefined && message.references.resolved.length > 0 && (
				<ul class="references" aria-label="Records this turn refers to">
					{message.references.resolved.map((reference) => (
						<li key={reference.ref} class={`reference reference-${reference.status.toLowerCase()}`}>
							<code>{reference.ref}</code>{' '}
							<span class="muted small">
								{reference.status === 'RESOLVED'
									? reference.summary
									: `${reference.status} — ${reference.reason}`}
							</span>
							{reference.drift === 'CHANGED' && (
								<span class="reference-drift small">
									{' '}
									· this record has changed since you looked at it
								</span>
							)}
						</li>
					))}
				</ul>
			)}

			{message.refs.length > 0 && (
				<Technical summary={`${message.refs.length} citation${message.refs.length === 1 ? '' : 's'}`}>
					<ul class="references" aria-label="Citations">
						{message.refs.map((ref) => (
							<li key={ref}>
								<code>{ref}</code>
							</li>
						))}
					</ul>
				</Technical>
			)}

			{message.role === 'SCOUT' && (
				<button type="button" class="button ghost small" onClick={() => onDiscuss(message)}>
					Discuss
				</button>
			)}
		</article>
	);
}

export function ChatView() {
	// THE SPACE CHAT REUSES THIS SURFACE, NOT A SECOND ONE. `/chat/space/<id>`
	// resolves that space's own conversation; the URL remains the source of the
	// scope, so a reload and a bookmark preserve it.
	const spaceId =
		route.value.destination === 'chat' && route.value.section === 'space'
			? route.value.recordId
			: null;
	// BUILT ONCE. A loadable created in the render body would be a new signal on
	// every render, and the view would read "loading" forever while the answer
	// sat in a signal nothing was subscribed to.
	const chat = useMemo(
		() =>
			createLoadable<MainChatResolution>(() =>
				spaceId === null ? resolveMainChat() : resolveSpaceChat(spaceId),
			),
		[spaceId],
	);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [olderCursor, setOlderCursor] = useState<number | null>(null);
	const [hasOlder, setHasOlder] = useState(false);
	const [loadingOlder, setLoadingOlder] = useState(false);
	const [pending, setPending] = useState<Pending>({ kind: 'none' });
	/** THE DRAFT THE SERVER ACCEPTED. The composer clears only a draft that
	 *  still matches it, so text typed while Scout works is never erased. */
	const [clearDraft, setClearDraft] = useState<
		{ nonce: number; question: string; materialIds: string[] } | undefined
	>(undefined);
	/** A DRAFT PUT BACK WHEN THE SUBMISSION FAILED BEFORE IT WAS ACCEPTED. The
	 *  composer is cleared at submission so the Owner can keep typing; a failed
	 *  send must not cost them the text or the attachments. */
	const [restoreDraft, setRestoreDraft] = useState<
		{ nonce: number; question: string; materials: AttachedMaterial[] } | undefined
	>(undefined);
	const restoreNonceRef = useRef(0);
	const [elapsed, setElapsed] = useState(0);
	const [notice, setNotice] = useState<string | null>(null);
	/** SCOUT IS COMPOSING AN INVITED TURN. A state (for the button) and a ref
	 *  (for the synchronous double-click guard) because a second click can
	 *  arrive before the disabled attribute is painted. */
	const [scoutTurnPending, setScoutTurnPending] = useState(false);
	const scoutTurnBusyRef = useRef(false);
	/** THE INVITATION'S NAME, KEPT ACROSS A FAILED PRESS. A retry must ask
	 *  about the same invitation, exactly as a retried question asks about the
	 *  same turn: if the first attempt reached the provider and its row was
	 *  written, the server's request-id lookup returns that row instead of
	 *  paying for a second turn. The id is cleared only once the server has
	 *  answered. */
	const scoutTurnRequestRef = useRef<string | null>(null);
	const [atBottom, setAtBottom] = useState(true);
	const [newCount, setNewCount] = useState(0);
	const listRef = useRef<HTMLDivElement | null>(null);
	const stickRef = useRef(true);
	const previousLengthRef = useRef(0);
	const suppressGrowthRef = useRef(false);
	/** The draft a "Discuss" action pre-fills. A reference into the same
	 *  conversation, never a second thread. */
	const [draft, setDraft] = useState<string | null>(null);
	/** THE RECORD THIS TURN IS ABOUT, when the Owner arrived from a view. Held as
	 *  a REF and never as text: what travels is which record, and the server
	 *  decides what it says. Cleared once the turn is sent, so a reload does not
	 *  silently re-attach it to the next thing the Owner types. */
	const [discussing, setDiscussing] = useState<string | null>(null);

	// A DISCUSSION ARRIVES AS A PATH, SO IT SURVIVES A RELOAD. `/chat/discuss/<ref>`
	// is a real address a view can link to, a person can bookmark, and a second
	// tab resolves to the same thing — none of which a value held in memory would
	// do. The ref is taken from the route and NOT from anything the page stored.
	useEffect(() => {
		if (route.value.destination !== 'chat') return;
		if (route.value.section !== DISCUSS_ROUTE_SECTION) return;
		const ref = route.value.recordId;
		if (ref === null || ref.length === 0) return;
		setDiscussing(ref);
	}, [route.value.section, route.value.recordId, route.value.destination]);

	const state = chat.state.value;
	const resolution = state.data;

	useEffect(() => {
		void chat.load();
	}, [chat]);

	// THE FIRST PAGE, ONCE THE SERVER HAS SAID WHICH CONVERSATION THIS IS.
	useEffect(() => {
		if (resolution === null) return;
		setMessages(resolution.messages);
		setOlderCursor(resolution.olderCursor);
		setHasOlder(resolution.hasOlder);
		previousLengthRef.current = resolution.messages.length;
		// OPENING A CHAT SHOWS THE NEWEST TURN. That is the reader's intent on
		// arrival, and it is not a jump away from anything.
		requestAnimationFrame(() => scrollToLatest('auto'));
	}, [resolution]);

	// THE ELAPSED CLOCK, WHICH IS THE ONLY HONEST PROGRESS A NON-STREAMING
	// BACKEND CAN OFFER. It says how long the request has been outstanding, not
	// how far along it is — this code cannot know that, and inventing it would
	// be the fake the brief forbids.
	useEffect(() => {
		if (pending.kind !== 'sending') return;
		const started = pending.startedAt;
		const timer = setInterval(() => setElapsed(Date.now() - started), 1_000);
		return () => clearInterval(timer);
	}, [pending]);

	// FOLLOW THE CONVERSATION ONLY WHEN THE READER IS FOLLOWING IT.
	useEffect(() => {
		const next = messages.length;
		const previous = previousLengthRef.current;
		previousLengthRef.current = next;
		if (suppressGrowthRef.current) {
			suppressGrowthRef.current = false;
			return;
		}
		if (next <= previous) return;
		if (stickRef.current) {
			requestAnimationFrame(() => scrollToLatest('smooth'));
		} else {
			setNewCount((count) => count + (next - previous));
		}
	}, [messages.length]);

	function scrollToLatest(behavior: ScrollBehavior): void {
		const list = listRef.current;
		if (list === null) return;
		list.scrollTo({ top: list.scrollHeight, behavior });
		stickRef.current = true;
		setAtBottom(true);
		setNewCount(0);
	}

	function onScroll(): void {
		const list = listRef.current;
		if (list === null) return;
		const near =
			list.scrollHeight - list.scrollTop - list.clientHeight <= STICK_THRESHOLD_PX;
		stickRef.current = near;
		setAtBottom(near);
		if (near) setNewCount(0);
	}

	async function refreshNewest(): Promise<void> {
		const result = spaceId === null ? await resolveMainChat() : await resolveSpaceChat(spaceId);
		if (!result.ok) return;
		setMessages((current) => {
			const byId = new Map(current.map((message) => [message.messageId, message]));
			for (const message of result.data.messages) byId.set(message.messageId, message);
			return [...byId.values()].sort((a, b) => a.sequence - b.sequence);
		});
		setOlderCursor((current) => current ?? result.data.olderCursor);
		setHasOlder(result.data.hasOlder);
	}

	async function send(input: {
		question: string;
		materials: AttachedMaterial[];
		/** SET ONLY BY THE RECOVERY CONTROL. It tells the server the Owner has
		 *  decided to have an uncertain turn answered again, knowing the first
		 *  attempt may already have been billed. Nothing else in this view sets
		 *  it, and no retry path defaults it. */
		recoverAbandoned?: boolean;
	}): Promise<void> {
		if (resolution === null) return;
		// A FAILED SEND KEEPS ITS NAME, AND SO DOES AN UNCERTAIN ONE — BUT ONLY
		// WHEN THE DRAFT IS THE SAME TURN. Regenerating the id would turn a retry
		// into a second question; reusing it for edited text or different
		// attachments would answer a question the Owner no longer asked.
		const question = input.question.length > 0 ? input.question : '(see the attached material)';
		// A RETRY CARRIES WHAT THE FIRST ATTEMPT CARRIED. The attachments are
		// part of the turn, and answering the same question without them would
		// be a different answer wearing the same words.
		const materials =
			input.materials.length > 0
				? input.materials
				: pending.kind === 'failed' || pending.kind === 'uncertain'
					? pending.materials
					: [];
		const sameDraft =
			(pending.kind === 'failed' || pending.kind === 'uncertain') &&
			pending.question === question &&
			pending.materials.length === materials.length &&
			pending.materials.every(
				(material, index) => material.materialId === materials[index]?.materialId,
			);
		const requestId = sameDraft ? pending.requestId : mintRequestId();
		const draftQuestion = input.question;
		const draftMaterialIds = input.materials.map((material) => material.materialId);
		const confirmDraftSent = (): void =>
			setClearDraft((current) => ({
				nonce: (current?.nonce ?? 0) + 1,
				question: draftQuestion,
				materialIds: draftMaterialIds,
			}));
		const restoreFailedDraft = (): void => {
			restoreNonceRef.current += 1;
			setRestoreDraft({
				nonce: restoreNonceRef.current,
				question: draftQuestion,
				materials,
			});
		};
		setNotice(null);
		setPending({ kind: 'sending', requestId, question, materials, startedAt: Date.now() });
		// THE TURN IS ACCEPTED BY THIS VIEW AS SOON AS IT IS SUBMITTED. The
		// optimistic "sent" row above the composer is the visible acknowledgement;
		// holding the draft until the model answers leaves the Owner's own words
		// sitting in the box for the whole paid turn. A failure below restores it.
		confirmDraftSent();
		setElapsed(0);
		// SENDING IS THE ONE MOMENT A JUMP IS WANTED: the Owner just acted, so
		// the turn they sent belongs in view.
		requestAnimationFrame(() => scrollToLatest('smooth'));

		// THE REFERENCE GOES WITH THE TURN, AND ONLY AS A REFERENCE. There is no
		// field for record text: the server resolves `(kind, id)` against canonical
		// state when it answers, so a record that moved since the Owner looked is
		// reported as moved rather than quoted from a stale copy.
		const references = discussing === null ? [] : [{ ref: discussing }];
		const result = await sendTurn({
			conversationId: resolution.conversationId,
			question,
			clientRequestId: requestId,
			...(spaceId === null ? {} : { spaceId }),
			...(materials.length === 0
				? {}
				: { materialRefs: materials.map((material) => material.materialId) }),
			...(references.length === 0 ? {} : { references }),
			...(input.recoverAbandoned === true ? { recoverAbandoned: true } : {}),
		});

		if (!result.ok) {
			// AN ABORTED REQUEST IS NOT A CANCELLED ONE, and it is not reported
			// as a failure of Scout's either: nothing is known about what the
			// server did, so nothing is claimed.
			if (result.error.name === 'ScoutRequestAbandoned') {
				setNotice(
					'You stopped waiting. That does not stop anything Scout is doing — the turn may still be answered, and reading the conversation again will show it if it was.',
				);
				setPending({ kind: 'none' });
				return;
			}
			setPending({ kind: 'failed', requestId, question, materials, error: result.error });
			// THE FAILED DRAFT COMES BACK. The composer was cleared at submission;
			// restoring here means the Owner can edit and resend rather than
			// retyping a long question or re-attaching their document.
			restoreFailedDraft();
			return;
		}

		if (result.outcome.requestState === 'IN_FLIGHT') {
			// THE SERVER SAID THE FIRST ATTEMPT IS STILL RUNNING. Reporting this
			// as a failure would be the exact lie the request identity exists to
			// prevent, and starting another would spend twice.
			setPending({
				kind: 'inFlight',
				requestId,
				question,
				materials,
				note: result.outcome.note,
			});
			// THE DRAFT WAS CLEARED AT SUBMISSION. It is deliberately not cleared
			// again here: the Owner may have retyped the same words as a new draft
			// while Scout worked, and this response has no claim on that one.
			await refreshNewest();
			return;
		}

		if (result.outcome.requestState === 'UNCERTAIN') {
			// NOT A FAILURE AND NOT A WAIT. The server will not decide to spend
			// again on the Owner's behalf, so the decision is offered here — and
			// the turn is kept, with its request id, so accepting the offer asks
			// about the SAME turn rather than starting a new one.
			setPending({
				kind: 'uncertain',
				requestId,
				question,
				materials,
				note: result.outcome.note,
				how: result.outcome.recovery.how,
			});
			// THE TURN EXISTS ON THE SERVER, even though whether it was answered
			// cannot be said. The recovery control carries it from here, and the
			// draft was already cleared at submission.
			await refreshNewest();
			return;
		}

		setPending({ kind: 'none' });
		// ANSWERED OR REPLAYED: the turn is on record. The draft was cleared at
		// submission; clearing again here could erase a new draft that happens to
		// contain the same words.
		if (result.outcome.requestState === 'REPLAYED') {
			setNotice('That turn had already been answered, so Scout returned the answer it gave.');
		}
		// THE ATTACHMENT IS SPENT ONCE THE TURN EXISTS. Leaving it on would attach
		// the same record to the next thing the Owner typed, which they did not
		// ask for — and clearing the PATH rather than only the state is what stops
		// a reload from re-attaching it.
		if (discussing !== null) {
			setDiscussing(null);
			navigate({ destination: 'chat', section: null, recordId: null }, { replace: true });
		}
		await refreshNewest();
	}

	/**
	 * "SCOUT, CERITA" — ONE INVITATION, ONE PERSISTED SCOUT TURN.
	 *
	 * NOTHING IS SENT AS THE OWNER. The click is the event; the server records
	 * it as the trigger of a SCOUT row. The request id makes a retry the same
	 * invitation rather than a second one, and the ref guard stops a rapid
	 * double click from starting two model calls before the disabled state is
	 * painted.
	 */
	async function inviteScoutTurn(): Promise<void> {
		if (resolution === null || pending.kind === 'sending') return;
		if (scoutTurnBusyRef.current) return;
		scoutTurnBusyRef.current = true;
		setScoutTurnPending(true);
		setNotice(null);
		// THE SAME INVITATION ON A RETRY. A fresh id is minted only when no
		// earlier press is unresolved; the server's unique request id is what
		// makes the second press a question about the first one rather than a
		// second paid turn.
		const requestId = scoutTurnRequestRef.current ?? mintRequestId();
		scoutTurnRequestRef.current = requestId;
		const result = await scoutTurn({ clientRequestId: requestId });
		scoutTurnBusyRef.current = false;
		if (!result.ok) {
			// NO MESSAGE IS PERSISTED ON FAILURE. The notice says the turn did
			// not happen; the SAME invitation may be retried by pressing again
			// (the request id is kept, because the first attempt may have been
			// answered and then lost on the way back).
			setScoutTurnPending(false);
			setNotice(`Scout could not take the turn: ${result.error.message}`);
			return;
		}
		// THE SERVER HAS ANSWERED, so this invitation is resolved and the next
		// press is a new act.
		scoutTurnRequestRef.current = null;
		await refreshNewest();
		setScoutTurnPending(false);
		requestAnimationFrame(() => scrollToLatest('smooth'));
		if (result.data.duplicate) {
			setNotice('That invitation had already been answered, so Scout returned the turn it produced.');
		}
	}

	async function loadOlder(): Promise<void> {
		if (resolution === null || olderCursor === null || loadingOlder) return;
		setLoadingOlder(true);
		// THE SCROLL POSITION IS HELD ACROSS THE PREPEND. Without this the
		// browser keeps its offset from the top, and the Owner is thrown
		// hundreds of turns away from where they were reading.
		const list = listRef.current;
		const before = list === null ? 0 : list.scrollHeight;
		const result = await readOlderMessages(resolution.conversationId, olderCursor);
		setLoadingOlder(false);
		if (!result.ok) {
			setNotice(`Could not read further back: ${result.error.message}`);
			return;
		}
		suppressGrowthRef.current = true;
		setMessages((current) => {
			const byId = new Map(current.map((message) => [message.messageId, message]));
			for (const message of result.data.messages) byId.set(message.messageId, message);
			return [...byId.values()].sort((a, b) => a.sequence - b.sequence);
		});
		setOlderCursor(result.data.olderCursor);
		setHasOlder(result.data.hasOlder);
		requestAnimationFrame(() => {
			if (list !== null) list.scrollTop += list.scrollHeight - before;
		});
	}

	async function adopt(conversationId: string): Promise<void> {
		const result = await adoptMainChat(conversationId);
		if (!result.ok) {
			setNotice(`Scout did not adopt that conversation: ${result.error.message}`);
			return;
		}
		setNotice(
			'That conversation is now the Main Chat. Nothing was moved, copied or merged — the workspace points at the record that already existed, and the previous conversation remains readable in the archive.',
		);
		chat.state.value = { status: 'ready', data: result.data, error: null, readAt: new Date().toISOString() };
	}

	if (state.status === 'loading') {
		return (
			<section class="view">
				<p class="notice" role="status">
					Opening your Main Chat…
				</p>
			</section>
		);
	}

	if (state.status === 'failed' && state.error !== null) {
		const error = state.error;
		return (
			<section class="view">
				<PageHeader
					title="Main Chat"
					about={{
						purpose: 'The one conversation, where research is started and everything is discussed.',
						limits: <p>This page could not be opened, so it can show nothing at all.</p>,
					}}
				/>
				<div class="panel failure" role="alert">
					<h2>
						{isAuthenticationFailure(error)
							? 'Not signed in to Scout'
							: isNotTheOwner(error)
								? 'This account is not Scout’s Owner'
								: 'Scout did not answer'}
					</h2>
					<p>{error.message}</p>
					<Technical>
						<p class="small">
							<code>{error.name}</code>
							{error.status > 0 && ` · HTTP ${error.status}`}
						</p>
					</Technical>
					<button type="button" class="button" onClick={() => void chat.load()}>
						Try again
					</button>
				</div>
			</section>
		);
	}

	if (resolution === null) return null;

	const provenance = (
		<>
			<p class="small">
				This conversation is <code>{resolution.conversationId}</code> —{' '}
				{resolution.decidedBy === 'ADOPTED' ? 'adopted from an earlier record' : 'created for you'}.
			</p>
			<p class="small">{resolution.evidence}</p>
		</>
	);

	return (
		<section class="view chat" aria-labelledby="chat-heading">
			<PageHeader
				id="chat-heading"
				title={
					spaceId === null
						? 'Main Chat'
						: `${(resolution as SpaceChatResolution).space?.name ?? 'Research Space'} — researcher chat`
				}
				about={{
					purpose:
						spaceId === null
							? 'This is the one conversation. Research, evidence and findings are views beside it, and each can hand a reference into here rather than starting a thread of its own.'
							: 'This is the Research Space’s dedicated conversation. The researcher reads its Vision, Mission and Perspective here. Ordinary chat does not start an autonomous research window; the space’s Run Research Now control or its schedule does that.',
					how: (
						<>
							<p>
								Type a message and press Enter; Shift+Enter starts a new line. A question is up to
								4,000 characters — longer material belongs in “Attach long text”, where Scout keeps
								all of it and reads the part that matters.
							</p>
							<p>
								{spaceId === null
									? 'To start research, say what to investigate and for how long in a sentence. The research strip above the composer shows the live window while it runs, and the finished record is in Research and Discoveries.'
									: 'To authorize autonomous work, open the space and use “Run Research Now” (or configure its schedule). A message here is conversation with the dedicated researcher; it does not silently open a research window.'}
							</p>
						</>
					),
					limits: (
						<p>
							Research results are deliberately not posted into this conversation as reports. What
							arrives here is a concise completion notice with a link to the record.
						</p>
					),
				}}
			/>

			{spaceId !== null && (
				<p class="small muted">
					Research Space chat. <a href={`/research/spaces/${encodeURIComponent(spaceId)}`}>Back to the space</a>.
					This conversation is private to {spaceId}; it is not the Main Chat and it is not another space’s evidence.
				</p>
			)}
			{/* THE SERVER'S RESOLUTION IS ON THE RECORD, ONE PRESS AWAY. Which
			    conversation this is used to be the first line of the page; it is
			    reference material, so it belongs in a technical block — but the
			    IDENTITY is stated in the summary, because "the client picked the
			    same conversation" is a claim a reader must be able to check without
			    opening anything. */}
			<details class="technical chat-provenance">
				<summary>
					Which conversation this is — <code>{resolution.conversationId}</code>
				</summary>
				<div class="technical-body">{provenance}</div>
			</details>

			{/* A REQUIRED OWNER DECISION STAYS IN THE OPEN. The provenance is
			    reference material and lives in About; an unresolved choice about
			    WHICH conversation is the Main Chat is an action, and hiding it
			    would leave the Owner with no way to take it. */}
			{resolution.candidates !== undefined && resolution.candidates.length > 0 && (
				<details class="panel chat-provenance" open>
					<summary>Scout could not tell which earlier conversation is yours, so it did not choose.</summary>
					<ul class="small">
						{resolution.candidates.map((candidate) => (
							<li key={candidate.conversationId}>
								{candidate.title} · {candidate.turns} turns{' '}
								<code>{candidate.conversationId}</code>{' '}
								<button
									type="button"
									class="button ghost small"
									onClick={() => void adopt(candidate.conversationId)}
								>
									Use this one
								</button>
							</li>
						))}
					</ul>
				</details>
			)}

			{notice !== null && (
				<p class="panel small" role="status">
					{notice}
				</p>
			)}

			<div
				class="turns"
				ref={listRef}
				onScroll={onScroll}
				aria-live="polite"
				aria-label="Conversation"
			>
				{hasOlder && (
					<div class="load-older">
						<button
							type="button"
							class="button ghost"
							disabled={loadingOlder}
							onClick={() => void loadOlder()}
						>
							{loadingOlder ? 'Reading back…' : 'Read further back'}
						</button>
					</div>
				)}
				{messages.length === 0 && pending.kind === 'none' && (
					<div class="chat-welcome">
						<span class="eyebrow">Scout · research workspace</span>
						<h2>What should we investigate?</h2>
						<p>
							Ask a question, paste something to read, or describe a research window and how long
							it may run. Scout answers in this conversation and writes the research record
							beside it.
						</p>
						<ul class="chat-start-list">
							<li>
								<span class="chat-start-key">ask</span> A question Scout can answer from the
								record or from public sources.
							</li>
							<li>
								<span class="chat-start-key">attach</span> Long text or a file — Scout keeps
								all of it and reads the part that matters.
							</li>
							<li>
								<span class="chat-start-key">authorize</span> “Research X for 30 minutes” opens a
								bounded window; the strip above the composer shows it while it runs.
							</li>
						</ul>
						<p class="muted small">
							There is no start button elsewhere: this conversation is the one place a research
							window is authorized.
						</p>
					</div>
				)}
				{messages.map((message) => (
					<Message
						key={message.messageId}
						message={message}
						onDiscuss={(discussed) =>
							setDraft(`About ${discussed.messageId} (turn ${discussed.sequence}): `)
						}
					/>
				))}

				{pending.kind === 'sending' && (
					<article class="turn turn-owner turn-waiting">
						<header class="turn-head">
							<span class="turn-who">You</span>
							<span class="turn-label">sent</span>
						</header>
						<div class="turn-body">{pending.question}</div>
						<PendingAttachments materials={pending.materials} />
						<p class="working" role="status">
							Scout is working on this — {Math.round(elapsed / 1000)}s so far. One request, one answer
							at the end; Scout’s answer will appear here when it is complete.
						</p>
					</article>
				)}

				{pending.kind === 'inFlight' && (
					<article class="turn turn-owner turn-waiting">
						<header class="turn-head">
							<span class="turn-who">You</span>
							<span class="turn-label">already being answered</span>
						</header>
						<div class="turn-body">{pending.question}</div>
						<PendingAttachments materials={pending.materials} />
						<p class="working small" role="status">
							{pending.note}
						</p>
					</article>
				)}

				{pending.kind === 'uncertain' && (
					<div class="panel failure" role="alert">
						<h3>The record cannot say whether this was answered</h3>
						<div class="turn-body">{pending.question}</div>
						<PendingAttachments materials={pending.materials} />
						<p>{pending.note}</p>
						<p class="small">
							Answering again is a decision, not a retry. The earlier attempt may have reached the
							reasoning provider and been billed before this deployment lost it, and nothing on
							record says whether it was — so this server will not start a second paid call on your
							behalf. If you want it answered anyway, say so.
						</p>
						<button
							type="button"
							class="button"
							onClick={() =>
								void send({
									question: pending.question,
									materials: pending.materials,
									recoverAbandoned: true,
								})
							}
						>
							Answer it again — I accept it may be a second paid call
						</button>
						<Technical summary="Scout’s own words">
							<p class="muted small">{pending.how}</p>
						</Technical>
					</div>
				)}

				{pending.kind === 'failed' && (
					<div class="panel failure" role="alert">
						<h3>Scout did not answer that</h3>
						<p>{pending.error.message}</p>
						<PendingAttachments materials={pending.materials} />
						<Technical>
							<p class="muted small">
								<code>{pending.error.name}</code>
								{pending.error.status > 0 && ` · HTTP ${pending.error.status}`}
							</p>
						</Technical>
						<p class="small">
							Sending again asks Scout about the <strong>same</strong> turn rather than starting a
							second one — so if the first attempt is still running, it will say so instead of
							answering twice.
						</p>
						<button
							type="button"
							class="button"
							onClick={() =>
								void send({ question: pending.question, materials: pending.materials })
							}
						>
							Send it again
						</button>
					</div>
				)}
			</div>

			{!atBottom && (
				<button
					type="button"
					class="jump-latest"
					onClick={() => scrollToLatest('smooth')}
				>
					Jump to latest{newCount > 0 ? ` · ${newCount} new` : ''}
					<span aria-hidden="true"> ↓</span>
				</button>
			)}

			<div class="chat-footer">
				{draft !== null && (
					<p class="small muted draft-note">
						Discussing a reference puts it in this same conversation — there is no second thread.{' '}
						<button type="button" class="button ghost small" onClick={() => setDraft(null)}>
							Dismiss
						</button>
					</p>
				)}

				{discussing !== null && (
					<div class="discussing" role="status">
						<span class="discussing-label">Discussing</span> <code>{discussing}</code>
						<button
							type="button"
							class="button ghost small"
							onClick={() => {
								setDiscussing(null);
								navigate({ destination: 'chat', section: null, recordId: null }, { replace: true });
							}}
						>
							Remove
						</button>
					</div>
				)}

				{/* THE STATUS LINE, IN THE MANNER OF THE CLI THE OWNER NAMED. It
				    states only facts the view holds: which conversation, how many
				    turns are loaded, and whether the composer is waiting. It does
				    not claim Scout is "thinking" or that a model is streaming —
				    neither is observable here. */}
				<div class="chat-status-line" aria-live="polite">
					<span class="chat-status-left">
						<span>Scout</span>
						<span>{resolution.conversationId}</span>
						<span>{messages.length} turns</span>
					</span>
					<span class="chat-status-right">
						{pending.kind === 'sending' ? (
							<span>waiting for Scout…</span>
						) : pending.kind === 'inFlight' ? (
							<span>the turn is still running</span>
						) : (
							<span>Enter sends · Shift+Enter newline</span>
						)}
					</span>
				</div>

				<ResearchStrip onEnded={() => void refreshNewest()} />

				<Composer
					disabled={resolution === null}
					sending={pending.kind === 'sending'}
					onSend={(input) => void send(input)}
					clearDraft={clearDraft}
					restoreDraft={restoreDraft}
					// AN INVITED TURN IS A MAIN-CHAT ACT. A space's researcher is
					// invited by its own RUN authorization, not by this button,
					// which posts to the Main Chat's endpoint.
					onScoutTurn={spaceId === null ? () => void inviteScoutTurn() : undefined}
					scoutTurnPending={spaceId === null ? scoutTurnPending : undefined}
				/>
			</div>
		</section>
	);
}
